import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Group,
  Skeleton,
  Tabs,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { closeModal, openModal } from "@mantine/modals";
import { useEffect, useState, useCallback } from "react";
import {
  BsBank,
  BsCashCoin,
  BsCheck2,
  BsCreditCard,
  BsCreditCardFill,
} from "react-icons/bs";
import { MdClose, MdContactless } from "react-icons/md";
import { ModalId, ModalStages } from "src/utils/constants";
import Terminal from "./Terminal";
import {
  UnsavedMethod,
  useCatalogContext,
} from "src/api/context/CatalogContext";
import {
  GetClientPaymentMethodsManyQuery,
  PaymentMethodType,
  useGetClientPaymentMethodsManyLazyQuery,
} from "src/generated/graphql";
import { ISelectedPayment } from "src/components/PaymentSelection";

const UNSAVED_METHODS = [
  UnsavedMethod.Card,
  UnsavedMethod.Terminal,
  UnsavedMethod.ACH,
  UnsavedMethod.Cash,
];

const SignedAgreement_Methods = [
  UnsavedMethod.Card,
  UnsavedMethod.ACH,
  //UnsavedMethod.Terminal,
];
interface PaymentMethodsProps {
  submitUnsavedMethod?: (e: UnsavedMethod) => void;
  setSelectedPaymentMethod: (e: ISelectedPayment) => void;
  handleModalClose: () => void;
  selectedPaymentId?: string;
  clientId: string;
  permittedMethods?: UnsavedMethod[];
}
interface SavePaymentMethodsProps {
  selectedPaymentID: string | null;
  setSelectedPaymentID: (e: string) => void;
  paymentMethods: GetClientPaymentMethodsManyQuery["clientPaymentMethodsMany"];
}

interface UnsavedPaymentMethodsProps {
  otherPaymentID: UnsavedMethod | null;
  setOtherPaymentID: (e: UnsavedMethod | null) => void;
  setselectedTerminalId: (e: string) => void;
  permittedMethods: UnsavedMethod[];
}

const tabs = {
  saved: {
    label: "Saved Card / Account",
    value: "saved",
  },
  others: {
    label: "Other",
    value: "others",
  },
};

const PaymentMethod = ({
  setSelectedPaymentMethod,
  handleModalClose,
  selectedPaymentId,
  clientId,
  permittedMethods = SignedAgreement_Methods,
  submitUnsavedMethod,
}: PaymentMethodsProps) => {
  const theme = useMantineTheme();
  const {
    selectedTerminalId,
    setSelectedUnsavedMethod,
    setselectedTerminalId,
  } = useCatalogContext();
  const [activeTab, setActiveTab] = useState<string | null>(tabs.saved.value);
  const [selectedPaymentID, setSelectedPaymentID] = useState<string | null>(
    selectedPaymentId || null
  );
  const [otherPaymentID, setOtherPaymentID] = useState<UnsavedMethod | null>(
    null
  );

  useEffect(() => {
    if (activeTab === tabs.saved.value) {
      setOtherPaymentID(null);
      setSelectedUnsavedMethod(undefined);
    } else {
      setSelectedPaymentID(null);
      setSelectedUnsavedMethod(otherPaymentID || undefined);
    }
  }, [selectedPaymentID, otherPaymentID]);

  useEffect(() => {
    // Terminal selected marked Card terminal selected
    if (selectedTerminalId) {
      setActiveTab("others");
      setOtherPaymentID(UnsavedMethod.Terminal);
      setSelectedUnsavedMethod(UnsavedMethod.Terminal);
    }
  }, [selectedTerminalId]);

  const [getPayments, { data: paymentMethods, loading: paymentsLoading }] =
    useGetClientPaymentMethodsManyLazyQuery({});

  useEffect(() => {
    getPayments({ variables: { clientId } });
  }, []);

  let bg = theme.colors.secondary[2];
  let color = theme.colors.secondaryTextColor[6];

  return (
    <div
      style={{
        width: "100%",
        minHeight: 500,
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
      }}
    >
      <Flex
        align={"center"}
        justify={"space-between"}
        style={{
          padding: "10px 20px",
          marginBottom: "10px",
          borderBottom: `2px solid ${theme.colors.light[5]}`,
        }}
      >
        <Text size={22} fw={500}>
          Payment Methods
        </Text>
        <ActionIcon size="lg" onClick={handleModalClose}>
          <MdClose size={20} />
        </ActionIcon>
      </Flex>
      <Tabs
        value={activeTab}
        onTabChange={setActiveTab}
        mx={20}
        styles={{
          tabsList: {
            color,
            padding: "0 15px",
          },
          tab: {
            marginBottom: -2.5,
            fontWeight: 500,
            borderBottomWidth: 3,
          },
          panel: {
            padding: 15,
          },
        }}
      >
        <Tabs.List>
          {Object.values(tabs)?.map((key) => (
            <Tabs.Tab
              value={key.value}
              style={{
                color: theme.colors.secondaryTextColor[2],
              }}
              sx={{
                "&:hover": {
                  color: `${theme.colors.primary[4]} !important`,
                  borderColor: `${theme.colors.primary[4]} !important`,
                },
              }}
            >
              {key.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <Tabs.Panel value="saved">
          {paymentsLoading ? (
            <Skeleton h={70} mt={"xl"} />
          ) : !paymentMethods?.clientPaymentMethodsMany.length ? (
            <Text mt={"xl"} fw={600} align="center" size={20} mb={"xl"}>
              No Save Card Found
            </Text>
          ) : (
            <SavedPaymentMethods
              selectedPaymentID={selectedPaymentID}
              setSelectedPaymentID={setSelectedPaymentID}
              paymentMethods={paymentMethods?.clientPaymentMethodsMany || []}
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="others">
          <OtherPaymentMethods
            otherPaymentID={otherPaymentID}
            setOtherPaymentID={setOtherPaymentID}
            permittedMethods={permittedMethods}
            setselectedTerminalId={setselectedTerminalId}
          />
        </Tabs.Panel>
      </Tabs>

      <Flex direction={"column"} align={"center"}>
        <Button
          radius="lg"
          variant="filled"
          onClick={() => {
            const selectedMethod =
              paymentMethods?.clientPaymentMethodsMany.find(
                (e) => e.id === selectedPaymentID
              );
            setSelectedPaymentMethod(selectedMethod || null);
            if (otherPaymentID && submitUnsavedMethod) {
              submitUnsavedMethod(otherPaymentID);
            }
          }}
          disabled={
            (activeTab === tabs.saved.value && selectedPaymentID === null) ||
            (activeTab === tabs.others.value && otherPaymentID === null)
          }
          w={"100%"}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
        >
          {"Confirm"}
        </Button>
      </Flex>
    </div>
  );
};

const SavedPaymentMethods = ({
  selectedPaymentID,
  setSelectedPaymentID,
  paymentMethods,
}: SavePaymentMethodsProps) => {
  const theme = useMantineTheme();
  return (
    <Flex
      my={25}
      py={10}
      direction={"column"}
      bg={theme.colors.secondary[6]}
      style={{ borderRadius: 5 }}
    >
      {paymentMethods?.map((method, index) => {
        return (
          <Flex
            p={10}
            align={"center"}
            justify={"space-between"}
            key={method.id}
            style={{
              borderBottom:
                index < paymentMethods?.length - 1
                  ? `1px solid ${theme.colors.secondary[0]}`
                  : undefined,
            }}
          >
            <Flex align={"center"}>
              <Box
                w={40}
                h={40}
                style={{
                  borderRadius: 5,
                  background: theme.colors.primary[6],
                }}
              >
                <Flex w={"100%"} h={"100%"} align={"center"} justify={"center"}>
                  {method.type === PaymentMethodType.Card ? (
                    <BsCreditCardFill
                      size={25}
                      color={theme.colors.primaryTextColor[6]}
                    />
                  ) : (
                    <BsBank
                      size={25}
                      color={theme.colors.primaryTextColor[6]}
                    />
                  )}
                </Flex>
              </Box>
              <Flex direction={"column"} mx={10}>
                <Text size={16} fw={500}>
                  {method.type === PaymentMethodType.Card
                    ? method.merchant
                    : "Bank Account"}
                </Text>
                <Group spacing={4}>
                  <Text size={16} fw={400}>
                    {"Ending in "}
                  </Text>
                  <Text size={16} fw={500}>
                    {method.mask?.substring(method.mask.length - 4)}
                  </Text>
                </Group>
                {method.isPrimary ? <Text size={14}>Primary</Text> : null}
              </Flex>
            </Flex>
            <Button
              variant="filled"
              onClick={() => setSelectedPaymentID(method.id)}
              disabled={
                selectedPaymentID !== null && selectedPaymentID === method.id
              }
              leftIcon={selectedPaymentID === method.id && <BsCheck2 />}
              sx={{
                backgroundColor:
                  selectedPaymentID !== null && selectedPaymentID === method.id
                    ? `${theme.colors.secondary[4]} !important`
                    : undefined,
                "&:hover": {
                  backgroundColor:
                    selectedPaymentID !== null &&
                    selectedPaymentID === method.id
                      ? undefined
                      : theme.colors.primary[4],
                },
              }}
            >
              {selectedPaymentID !== null && selectedPaymentID === method.id
                ? " Selected"
                : "Select"}
            </Button>
          </Flex>
        );
      })}
    </Flex>
  );
};

const OtherPaymentMethods = ({
  otherPaymentID,
  setOtherPaymentID,
  setselectedTerminalId,
  permittedMethods,
}: UnsavedPaymentMethodsProps) => {
  const theme = useMantineTheme();

  const handleModalTerminal = useCallback(() => {
    openModal({
      modalId: ModalId.SelectTerminal,
      closeOnClickOutside: false,
      withCloseButton: false,
      children: <Terminal onClose={() => closeModal(ModalId.SelectTerminal)} />,
      styles: {
        body: {
          minHeight: "500px",
        },
      },
    });
  }, []);

  return (
    <Flex
      my={25}
      py={10}
      direction={"column"}
      bg={theme.colors.secondary[6]}
      style={{ borderRadius: 5 }}
    >
      {permittedMethods.map((method, index) => {
        return (
          <Flex
            p={10}
            align={"center"}
            justify={"space-between"}
            key={method + index}
            style={{
              borderBottom:
                index < permittedMethods?.length - 1
                  ? `1px solid ${theme.colors.secondary[0]}`
                  : undefined,
            }}
          >
            <Box
              w={40}
              h={40}
              style={{
                borderRadius: 5,
                background: theme.colors.primary[6],
              }}
            >
              <Flex w={"100%"} h={"100%"} align={"center"} justify={"center"}>
                {method === UnsavedMethod.Card ? (
                  <BsCreditCardFill
                    size={25}
                    color={theme.colors.primaryTextColor[6]}
                  />
                ) : method === UnsavedMethod.Cash ? (
                  <BsCashCoin
                    size={25}
                    color={theme.colors.primaryTextColor[6]}
                  />
                ) : (
                  <MdContactless
                    size={25}
                    color={theme.colors.primaryTextColor[6]}
                  />
                )}
              </Flex>
            </Box>
            <Text>{method}</Text>
            <Button
              variant="filled"
              onClick={() => {
                if (method === UnsavedMethod.Terminal) {
                  handleModalTerminal();
                } else {
                  setOtherPaymentID(method);
                  setselectedTerminalId("");
                }
              }}
              disabled={otherPaymentID !== null && otherPaymentID === method}
              leftIcon={otherPaymentID === method && <BsCheck2 />}
              sx={{
                backgroundColor:
                  otherPaymentID !== null && otherPaymentID === method
                    ? `${theme.colors.secondary[4]} !important`
                    : undefined,
                "&:hover": {
                  backgroundColor:
                    otherPaymentID !== null && otherPaymentID === method
                      ? undefined
                      : theme.colors.primary[4],
                },
              }}
            >
              {otherPaymentID !== null && otherPaymentID === method
                ? " Selected"
                : "Select"}
            </Button>
          </Flex>
        );
      })}
    </Flex>
  );
};

export default PaymentMethod;
